import React, { useState, useRef, useCallback, useEffect } from 'react'
import TouchRipple from '@material-ui/core/ButtonBase'
import { useLocation } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'

const useStyles = makeStyles(({ palette, ...theme }) => ({
    expansionPanel: {
        overflow: 'hidden',
        transition: 'max-height 0.3s ease',
        maxHeight: 0,
    },
    expansionPanelOpen: {
        maxHeight: 1000, // значение заведомо больше любого подменю
    },
    expandIcon: {
        transition: 'transform 0.3s cubic-bezier(0, 0, 0.2, 1) 0ms',
        transform: 'rotate(90deg)',
    },
    collapseIcon: {
        transition: 'transform 0.3s cubic-bezier(0, 0, 0.2, 1) 0ms',
        transform: 'rotate(0deg)',
    },
    'expansion-panel': {
        overflow: 'hidden',
        transition: 'max-height 0.3s cubic-bezier(0, 0, 0.2, 1)',
    },
    highlight: {
        background: palette.primary.main,
    },
    navItem: {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderTopRightRadius: 10,
        borderBottomRightRadius: 10,
        transition: 'all 150ms ease-in',
        paddingLeft: 13,
        paddingRight: 8,
        paddingTop: 2,
        paddingBottom: 2,
        marginBottom: 0,
        '&:hover': {
            backgroundColor: '#fafafa',
        },
    },
    navItemOpen: {
        backgroundColor: '#fafafa',
        borderBottomRightRadius: 0,
    },
    compactNavItem: {
        width: 'var(--sidenav-compact-width)',
        overflow: 'hidden',
        '& $itemText': {
            display: 'none',
        },
    },
    compactIcon: {
        width: 24,
        minWidth: 24,
        height: 24,
        lineHeight: '24px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible',
        textAlign: 'center',
        color: '#101010',
        '&.MuiIcon-root': {
            fontSize: 20,
            lineHeight: 1,
            overflow: 'visible',
        },
        '&.material-symbols-outlined': {
            fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24",
        },
    },
    navSvgIcon: {
        width: 18,
        height: 18,
        objectFit: 'contain',
        display: 'block',
    },
    navFontIcon: {
        fontFamily: '"Material Symbols Outlined"',
        fontWeight: 400,
        fontStyle: 'normal',
        fontSize: 20,
        lineHeight: 1,
        letterSpacing: 'normal',
        textTransform: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
        wordWrap: 'normal',
        direction: 'ltr',
        WebkitFontSmoothing: 'antialiased',
        fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24",
        color: '#101010',
    },
    itemIcon: {},
    arrowWrap: {
        width: 20,
        minWidth: 20,
        height: 24,
        marginLeft: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible',
    },
    itemText: {
        fontSize: '0.875rem',
        paddingLeft: '0.8rem',
        display: 'inline-flex',
        alignItems: 'center',
        lineHeight: 1.2,
    },
    bulletIcon: {
        background: palette.text.secondary,
    },
    submenuOpen: {
        backgroundColor: '#fafafa',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 10,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
    },
    submenuInner: {
        paddingLeft: 8,
        paddingRight: 8,
        paddingTop: 0,
        paddingBottom: 0,
    },
}))

const MatxVerticalNavExpansionPanel = ({ item, children, mode }) => {
    const [collapsed, setCollapsed] = useState(true)
    const classes = useStyles()
    const elementRef = useRef(null)
    const componentHeight = useRef(0)
    const { pathname } = useLocation()
    const { name, icon, iconSrc, iconText, badge } = item

    const handleClick = () => {
        componentHeight.current = 0
        calculateHeight(elementRef.current)
        setCollapsed(!collapsed)
    }

    const calculateHeight = useCallback((node) => {
        let totalHeight = 0
        if (node.name !== 'child') {
            Array.from(node.children).forEach(child => {
                totalHeight += calculateHeight(child)
            })
        } else {
            totalHeight += node.scrollHeight
            }
        componentHeight.current = totalHeight
        return totalHeight
    }, [])

    useEffect(() => {
        if (!elementRef.current) return

        calculateHeight(elementRef.current)

        // OPEN DROPDOWN IF CHILD IS ACTIVE
        const hasActiveChild = Array.from(elementRef.current.querySelectorAll('a[href]'))
            .some((child) => child.getAttribute('href') === pathname)
        if (hasActiveChild) setCollapsed(false)
    }, [pathname, calculateHeight])

    return (
        <div>
            <TouchRipple
                className={clsx({
                    'flex justify-between h-44 w-full has-submenu compactNavItem whitespace-pre': true,
                    [classes.navItem]: true,
                    [classes.navItemOpen]: !collapsed,
                    [classes.compactNavItem]: mode === 'compact',
                    open: !collapsed,
                })}
                onClick={handleClick}
            >
                <div className="flex items-center">
                    {iconSrc && (
                        <span className={clsx('align-middle', classes.compactIcon)}>
                            <img src={iconSrc} alt="" className={classes.navSvgIcon} />
                        </span>
                    )}
                    {!iconSrc && icon && (
                        <span className={clsx('align-middle', classes.compactIcon, classes.navFontIcon)}>
                            {icon}
                        </span>
                    )}
                    {iconText && <div className={clsx('w-4 h-4 rounded bg-white ml-5 mr-2', classes.bulletIcon)}></div>}
                    <span className={clsx('align-middle sidenavHoverShow', classes.itemText)}>
                        {name}
                    </span>
                </div>
                {badge && (
                    <div
                        className={clsx(
                            `rounded bg-${item.badge.color} px-1 py-1px`,
                            'sidenavHoverShow',
                            classes.itemIcon
                        )}
                    >
                        {badge.value}
                    </div>
                )}
                <div
                    className={clsx({
                        'item-arrow': true,
                        [classes.itemIcon]: true,
                        [classes.arrowWrap]: true,
                        [classes.collapseIcon]: collapsed,
                        [classes.expandIcon]: !collapsed,
                    })}
                >
                    <span className={clsx('align-middle', classes.navFontIcon)} style={{ fontSize: 18 }}>
                        chevron_right
                    </span>
                </div>
            </TouchRipple>

            <div
                ref={elementRef}
                className={clsx({
                    [classes.expansionPanel]: true,
                    [classes.expansionPanelOpen]: !collapsed,
                    [classes.submenuOpen]: !collapsed,
                    [classes.submenuInner]: true,
                    submenu: true,
                    'sidenavHoverShow': mode === 'compact',
                })}
            >
                {children}
            </div>
        </div>
    )
}

export default MatxVerticalNavExpansionPanel
